import styled from 'styled-components';
import { margin, layout, variant, BoxShadowProps, ColorProps } from 'styled-system';
import { MarginProps, BorderProps, LayoutProps, PropsWithPseudo } from '../../../theme/styledProps';
import { Box } from '../Box/Box';

const COLUMN_PADDING = {
  small: '10px 12px',
  medium: '10px 24px',
  large: '10px 32px',
};

type ColumnSpacing = 'small' | 'medium' | 'large';
type TableVariant = 'embedded' | 'basic' | 'bordered';

export interface HtmlTableProps extends MarginProps, LayoutProps {
  columnSpacing?: ColumnSpacing;
  variant?: TableVariant;
  fixedHead?: boolean;
  edgePadding?: number;
}

export const HtmlTable = styled(Box)<HtmlTableProps>`
  overflow-y: auto;
  width: 100%;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.bg};
  font-family: ${({ theme }) => theme.fontFamily};
  font-weight: ${({ theme }) => theme.fontWeights[400]};
  font-size: ${({ theme }) => `${theme.fontSizes.body}px`};
  color: ${({ theme }) => theme.colors.neutral[700]};
  box-shadow: 0 0 4px rgba(55, 65, 81, 0.06), 0 2px 6px rgba(55, 65, 81, 0.1);

  @media print {
    box-shadow: none;
    border: none;
  }

  ${variant<BorderProps & BoxShadowProps, TableVariant>({
    variants: {
      embedded: {
        boxShadow: 'none',
        borderRadius: 'none',
        borderBottom: '1px solid',
        borderColor: 'neutral.200',
      },

      basic: {
        boxShadow: 'none',
        borderRadius: 'none',
        border: '1px solid',
        borderColor: 'neutral.200',
      },

      bordered: {
        borderRadius: 'medium',
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'neutral.200',
      },
    },
  })}

  table {
    width: 100%;
    border-spacing: 0;

    // Use PDFSafeTableHeader for the caption
    // It enhances PDF printing by repeating the caption on the next page
    caption {
      border: 1px solid ${({ theme }) => theme.colors.neutral['200']};
      padding: 12px 16px;
      font-weight: 600;
      font-size: ${({ theme }) => `${theme.fontSizes.h6}px`};
      line-height: ${({ theme }) => theme.lineHeights.h6};
      color: ${({ theme }) => theme.colors.neutral['700']};
      text-align: left;
      border-bottom: 0;
    }

    thead {
      tr:first-child {
        th,
        td {
          &:first-of-type {
            border-radius: ${({ variant: tableVariant }) => {
              if (tableVariant && !['embedded', 'basic'].includes(tableVariant)) {
                return '4px 0 0 0';
              }
              return '0';
            }};
          }
          &:last-of-type {
            border-radius: ${({ variant: tableVariant }) => {
              if (tableVariant && !['embedded', 'basic'].includes(tableVariant)) {
                return '0 4px 0 0';
              }
              return '0';
            }};
          }
        }
      }

      @media print {
        tr {
          th,
          td {
            border-top: 1px solid ${({ theme }) => theme.colors.neutral['200']};
            border-bottom: 1px solid ${({ theme }) => theme.colors.neutral['200']};
            border-right: 1px solid ${({ theme }) => theme.colors.neutral['200']};

            &:first-child {
              border-left: 1px solid ${({ theme }) => theme.colors.neutral['200']};
            }
          }
        }
      }

      tr {
        padding: 10px 16px;
        page-break-inside: avoid;

        th,
        td {
          &:not(td) {
            background: ${({ theme }) => theme.colors.neutral[100]};
          }
          padding: ${({ columnSpacing }) => COLUMN_PADDING[columnSpacing as ColumnSpacing]};
          white-space: nowrap;

          border-bottom: 1px solid ${({ theme }) => theme.colors.neutral['200']};

          tr:not(:first-child),
          td:not(:first-child) {
            border-top: 1px solid ${({ theme }) => theme.colors.neutral['200']};
          }

          & > * {
            color: ${({ theme }) => theme.colors.neutral[600]};
          }

          ${({ fixedHead, theme }) =>
            fixedHead &&
            `
              position: sticky;
              top: 0;
              box-shadow: inset 0px 1px ${theme.colors.neutral[200]}, inset 0px -1px ${theme.colors.neutral[200]};
            `}

          &:first-of-type {
            padding-left: ${({ edgePadding }) => edgePadding}px;
          }

          &:last-of-type {
            padding-right: ${({ edgePadding }) => edgePadding}px;
          }
        }
      }

      tr:first-of-type.hasCaption {
        th,
        td {
          background: ${({ theme }) => theme.colors.bg};
          padding: 12px 16px;
          font-weight: 600;
          font-size: ${({ theme }) => `${theme.fontSizes.h6}px`};
          line-height: ${({ theme }) => theme.lineHeights.h6};
          color: ${({ theme }) => theme.colors.neutral['700']};
          text-align: left;
          border-bottom: 0;
          border-radius: 4px 4px 0 0;
        }
      }
    }

    tbody {
      tr {
        padding: 10px 16px;
        page-break-inside: avoid;

        &:last-of-type {
          border-bottom: none;
          border-radius: ${({ variant: tableVariant }) => {
            if (tableVariant && !['embedded', 'basic'].includes(tableVariant)) {
              return '0 0 4px 4px';
            }
            return '0';
          }};
        }

        &:last-child {
          td {
            &:first-of-type {
              border-radius: 0 0 0 4px;
            }

            &:last-of-type {
              border-radius: 0 0 4px 0;
            }
          }
        }

        &:not(:last-child) {
          td {
            border-bottom: 1px solid ${({ theme }) => theme.colors.neutral['200']};
          }
        }

        td {
          padding: ${({ columnSpacing }) => COLUMN_PADDING[columnSpacing as ColumnSpacing]};

          &:first-of-type {
            padding-left: ${({ edgePadding }) => edgePadding}px;
          }

          &:last-of-type {
            padding-right: ${({ edgePadding }) => edgePadding}px;
          }

          @media print {
            border-bottom: ${({ theme }) => `1px solid ${theme.colors.neutral['200']}`};
            border-right: 1px solid ${({ theme }) => theme.colors.neutral['200']};

            &:first-child {
              border-left: 1px solid ${({ theme }) => theme.colors.neutral['200']};
            }
          }
        }
      }
    }
  }

  ${margin}
  ${layout}
`;

HtmlTable.defaultProps = {
  columnSpacing: 'medium',
  edgePadding: 16,
};

/**
 * When used with
 *
 * `<colgroup><col width="100%" /></colgroup>`
 *
 * a flex cell will stretch to fill all the available space.
 */
export const Cell = styled.td<{ isFlex?: boolean }>`
  max-width: ${({ isFlex }) => (isFlex ? '1px' : 'fit-content')};
`;

type ClickableTableRowVariant = 'clickable' | 'disabled';

export const ClickableTableRow = styled.tr<{
  isExpanded?: boolean;
  isEmbeddedMobile?: boolean;
  variant: ClickableTableRowVariant;
}>(
  ({ onClick }) => ({
    ...(onClick
      ? {
          userSelect: 'none',
          tabIndex: 0,
          role: 'button',
          outline: 'none',
          cursor: 'pointer',
        }
      : {}),
  }),

  ({ isExpanded, isEmbeddedMobile }) =>
    variant<PropsWithPseudo<ColorProps, '&:hover'>, ClickableTableRowVariant>({
      variants: {
        clickable: {
          bg: isExpanded ? 'primary.100' : 'bg',

          '&:hover': {
            bg: isEmbeddedMobile ? undefined : isExpanded ? 'primary.200' : 'neutral.100',
          },
        },

        disabled: {
          bg: 'neutral.50',
        },
      },
    }),
);

ClickableTableRow.defaultProps = {
  variant: 'clickable',
};
